// filepath: apps/web/src/features/proc/useInterventionControl.ts
//
// FR-BPM-18 — TanStack Query mutation hooks for the six intervention
// action endpoints (POST /api/processes/instances/{id}/intervention/{action}).
//
// Mirrors the useCancelInstance / useRetryStep pattern:
//   - generates an idempotency_key via crypto.randomUUID() per ARCH Rule 3;
//   - invalidates procKeys.failedInstances on success so the dashboard
//     row updates automatically;
//   - 4xx errors are surfaced via the typed InterventionError;
//   - 5xx / network errors mapped to ApiError.
//
// INV-1: the body is the InterventionRequest wire envelope;
// INV-5: the response carries identifiers + enum discriminators +
// at-timestamps only -- the SPA does not log or render process data.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/definitions/types";
import { useTenant } from "@/tenant/TenantProvider";
import { apiFetch } from "@/api";
import { procKeys } from "./types";

export type InterventionActionKind =
  | "skip"
  | "continue_from"
  | "repeat"
  | "change_remaining_work"
  | "finish"
  | "cancel";

export interface InterventionRequest {
  idempotency_key: string;
  scope: string;
  target_node_id?: string;
  target_branch_id?: string;
}

export interface InterventionOutcome {
  intervention_id: string;
  action: InterventionActionKind;
  disposition: string;
  new_state: string;
  new_row_version: number;
  new_generation: number;
  audit_id: string;
  event_id: string;
  applied_at: string;
}

export interface InterventionVariables {
  instanceId: string;
  action: InterventionActionKind;
  scope: string;
  targetNodeID?: string;
  targetBranchID?: string;
}

export interface InterventionError extends Error {
  status: number;
  code:
    | "not_authorized"
    | "invalid_target"
    | "unsupported_action"
    | "unsupported_domain_action"
    | "row_version_conflict"
    | "invalid_scope"
    | "intervention_failed"
    | "network_error";
}

const actionURLSegment: Record<InterventionActionKind, string> = {
  skip: "skip",
  continue_from: "continue-from",
  repeat: "repeat",
  change_remaining_work: "change-remaining-work",
  finish: "finish",
  cancel: "cancel",
};

export function useInterventionControl(
  opts?: {
    onSuccess?: (outcome: InterventionOutcome) => void;
    onError?: (err: InterventionError) => void;
  },
  apiBase = "/api",
): {
  mutate: (variables: InterventionVariables) => void;
  mutateAsync: (
    variables: InterventionVariables,
  ) => Promise<InterventionOutcome>;
  isPending: boolean;
  isError: boolean;
  error: InterventionError | null;
} {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();

  const mutation = useMutation<
    InterventionOutcome,
    InterventionError,
    InterventionVariables
  >({
    mutationFn: async ({
      instanceId,
      action,
      scope,
      targetNodeID,
      targetBranchID,
    }: InterventionVariables) => {
      const url = `${apiBase}/processes/instances/${encodeURIComponent(instanceId)}/intervention/${actionURLSegment[action]}`;
      const idempotencyKey = crypto.randomUUID();
      const reqBody: InterventionRequest = {
        idempotency_key: idempotencyKey,
        scope,
        ...(targetNodeID !== undefined && { target_node_id: targetNodeID }),
        ...(targetBranchID !== undefined && {
          target_branch_id: targetBranchID,
        }),
      };

      let response: Response;
      try {
        // ISS-377 round 3 R2-B2.5: migrated to apiFetch. The
        // body field `idempotency_key` is part of the server
        // contract (ARCH Rule 3); the apiFetch `idempotencyKey`
        // flag is intentionally NOT passed (the action
        // handler reads the body field, not the X-Idempotency-Key
        // header) so a redundant header is not attached.
        response = await apiFetch(url, {
          method: "POST",
          init: {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(reqBody),
          },
        });
      } catch (networkErr) {
        const e: InterventionError = Object.assign(
          new Error(
            networkErr instanceof Error ? networkErr.message : "network error",
          ),
          { status: 0, code: "network_error" as const },
        );
        throw e;
      }

      if (!response.ok) {
        const body = (await safeReadJson(response)) as {
          error?: string;
          code?: InterventionError["code"];
        } | null;
        const e: InterventionError = Object.assign(
          new Error(body?.error ?? response.statusText),
          {
            status: response.status,
            code:
              body?.code ??
              (`http_${response.status}` as InterventionError["code"]),
          },
        );
        throw e;
      }

      return (await response.json()) as InterventionOutcome;
    },
    onSuccess: (outcome, variables) => {
      void queryClient.invalidateQueries({
        queryKey: procKeys.failedInstances(tenantSlug),
      });
      void queryClient.invalidateQueries({
        queryKey: procKeys.stepLog(tenantSlug, variables.instanceId),
      });
      opts?.onSuccess?.(outcome);
    },
    onError: (err) => {
      opts?.onError?.(err);
    },
    retry: (count, err) => {
      // Never auto-retry 4xx -- caller decides whether to retry.
      if (err.status >= 400 && err.status < 500) return false;
      return count < 2;
    },
  });

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  };
}

async function safeReadJson(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

export { ApiError };
